/* eslint-disable react-hooks/rules-of-hooks */
import { EpcLoanActionTypes, PersonaAccessResponse } from '@frontend/epc';
import { getRequestLockActionType } from '../../../../common/app-helpers';
import {
  RequestActionType,
  RequestLockStatus,
} from '../../../../common/constants/enumTypes';
import { useOriginalLoanInformationSelector } from '../../../../data/useSelectors';

const epcLoanActionMap = {
  NewLock: EpcLoanActionTypes.LockRequest,
  ActiveNewLock: EpcLoanActionTypes.LockRequest,
  ActiveLockUpdate: EpcLoanActionTypes.RePrice,
  ExpiredReLock: EpcLoanActionTypes.Relock,
  CancelledReLock: EpcLoanActionTypes.Relock,
};

export const getTPOActionAccessibility = (
  allowedActions,
  requestActionType,
) => {
  const defaultValue = 'ENABLED';
  let filterItem: any[] = [];
  if (allowedActions) {
    filterItem = allowedActions.filter(
      (item) => item.action === requestActionType,
    );
  }
  return filterItem ? filterItem[0]?.accessibility : defaultValue;
};

export const getPersonaActionAccessibility = (
  allowedActions: PersonaAccessResponse[],
  requestActionType: EpcLoanActionTypes,
): string => {
  const defaultValue = 'ENABLE';
  let filterItem: PersonaAccessResponse[] = [];
  if (allowedActions) {
    filterItem = allowedActions.filter(
      (item) => item.loanAction === requestActionType,
    );
  }
  return filterItem ? filterItem[0]?.restrictionType : defaultValue;
};

export const isRequestLockButtonVisible = (
  applicationName,
  loanData,
  allowedActions,
) => {
  let isVisible = true;
  if (
    applicationName &&
    applicationName === 'TPO' &&
    getTPOActionAccessibility(allowedActions, 'LockRequest') === 'HIDDEN'
  ) {
    isVisible = false;
  }

  const requestLockActionType = getRequestLockActionType(loanData);
  /**
   * unfortunately the request lock button is used for multiple operations
   * so based on the request lock action type we need to determine if the permission applies to lock action type
   * TODO check in with product for better messaging
   */
  if (requestLockActionType) {
    const epcActionType: EpcLoanActionTypes =
      epcLoanActionMap[requestLockActionType];
    if (
      epcActionType &&
      getPersonaActionAccessibility(allowedActions, epcActionType) === 'Hide'
    ) {
      isVisible = false;
    }
  }

  return isVisible;
};

export const isSaveToLoanButtonVisible = (
  applicationName,
  allowedActions,
  tpo,
  clientSettingsData,
) => {
  const hideLockRequest = clientSettingsData?.hideLockRequest;
  if (hideLockRequest) {
    return false;
  }

  if (applicationName && applicationName === 'TPO') {
    const isHidden =
      getTPOActionAccessibility(allowedActions, 'FloatLock') === 'HIDDEN';
    if (isHidden || tpo?.underwritingDelegated || hideLockRequest) return false;
  }
  return true;
};

export const isRequestLockButtonDisabled = (
  rateStale,
  loanData,
  applicationName,
  allowedActions,
  isDisableRequestLockButton,
  shutDownAllowLockRequestValidation,
  disableLock,
) => {
  if (disableLock) {
    return true;
  }

  const { requestLockStatus, requestAction } = loanData;
  const isAllowLockRequest = shutDownAllowLockRequestValidation();
  if (
    applicationName &&
    applicationName === 'TPO' &&
    getTPOActionAccessibility(allowedActions, 'LockRequest') === 'DISABLED'
  ) {
    return true;
  }
  const requestLockActionType = getRequestLockActionType(loanData);
  const epcActionType = requestLockActionType
    ? epcLoanActionMap[requestLockActionType]
    : null;
  /**
   * unfortunately the request lock button is used for multiple operations
   * so based on the request lock action type we need to determine if the permission applies to lock action type
   * TODO check in with product for better messaging
   */
  const isPersonaDisabled =
    epcActionType &&
    getPersonaActionAccessibility(allowedActions, epcActionType) === 'Disable';

  const isDisabled =
    (requestLockStatus === RequestLockStatus.NoStatus &&
      requestAction === RequestActionType.ValidateReLock) ||
    (requestLockStatus === RequestLockStatus.NotLocked &&
      requestAction === RequestActionType.ValidateReLock) ||
    (requestLockStatus === RequestLockStatus.Expired &&
      requestAction === RequestActionType.QuoteRequest) ||
    (requestLockStatus === RequestLockStatus.Cancelled &&
      requestAction === RequestActionType.QuoteRequest) ||
    isDisableRequestLockButton ||
    isAllowLockRequest ||
    isPersonaDisabled;

  return isDisabled;
};

export const isFloatButtonVisible = (
  allowedActions: PersonaAccessResponse[],
  clientSettingsData,
) => {
  let isFloatButtonVisible = clientSettingsData?.icePpeFloat;
  if (
    getPersonaActionAccessibility(
      allowedActions,
      EpcLoanActionTypes.FloatLock,
    ) === 'Hide'
  ) {
    isFloatButtonVisible = false;
  }

  return isFloatButtonVisible;
};

export const isFloatButtonDisabled = (
  allowedActions: PersonaAccessResponse[],
) => {
  const { lockRequests } = useOriginalLoanInformationSelector() as any;

  const lockRequestIsDenied = lockRequests?.every(
    (req) => req.requestedStatus === 'Denied',
  );

  const isDisabledFromLock = lockRequests?.length && !lockRequestIsDenied;
  const isDisabledFromPersona =
    getPersonaActionAccessibility(
      allowedActions,
      EpcLoanActionTypes.FloatLock,
    ) === 'Disable';

  return isDisabledFromLock || isDisabledFromPersona;
};

export const isSaveToLoanButtonDisabled = (
  loanData,
  applicationName,
  allowedActions,
  disableLock,
) => {
  if (disableLock) {
    return true;
  }
  if (applicationName && applicationName === 'TPO') {
    const isDisabled =
      getTPOActionAccessibility(allowedActions, 'FloatLock') === 'DISABLED';
    if (isDisabled) return true;
    const { requestLockStatus, requestAction } = loanData;
    return (
      requestAction === RequestActionType.ValidateReLock &&
      [RequestLockStatus.Expired, RequestLockStatus.Cancelled].includes(
        requestLockStatus,
      )
    );
  }
  return false;
};

export const getEPCActionType = (requestLockActionType, autolocked) => {
  const { buyside, sellSideBestPrice, sellSideSameProduct } = autolocked;

  if (buyside || sellSideBestPrice || sellSideSameProduct) {
    return 'LOCK_CONFIRM';
  }

  if (
    requestLockActionType === 'NewLock' ||
    requestLockActionType === 'ActiveNewLock'
  ) {
    return 'LOCK';
  }

  //TODO: enable this when EPC supports updating of loan elements
  //with  UPDATE action. 
  //Currently, they have a filter schema setup that excludes
  //certain elements from being updated. - Meeting held on 08/13/2025
  //EPPS-80957
  /*if (requestLockActionType === 'ActiveLockUpdate') {
    return 'UPDATE';
  }*/

  return 'RELOCK';
};

export const checkLockRequested = (loanData, lockRequests) => {
  const { requestAction, requestLockStatus } = loanData;
  const hasLockRequest = lockRequests?.some(
    (request) => request.requestedStatus === 'Requested',
  );
  return (
    hasLockRequest &&
    requestAction === RequestActionType.QuoteRequest &&
    requestLockStatus === RequestLockStatus.NotLocked
  );
};
